/**
 * CarmView domain objects — C-arm angle presets for the 3D viewport.
 *
 * A CarmView captures a named camera position defined by craniocaudal (CC)
 * and oblique (OBL) angles, together with the full VTK camera transform
 * needed to reproduce that view.
 *
 * Immutable after construction.
 */

import type { ICarmAngleData, Vec3 } from './types';
import type { CameraState } from '../viewports/types';

export interface ICameraTransform {
    readonly position: Vec3;
    readonly focal: Vec3;
    readonly up: Vec3;
    readonly angle: number;
}

export function cameraStateFromTransform(t: ICameraTransform): CameraState {
    return {
        position: t.position,
        focalPoint: t.focal,
        viewUp: t.up,
        viewAngle: t.angle,
        parallelScale: 1,
    };
}

export function cameraTransformFromState(s: CameraState): ICameraTransform {
    return {
        position: s.position,
        focal: s.focalPoint,
        up: s.viewUp,
        angle: s.viewAngle,
    };
}

export interface ICarmView {
    readonly id: string;
    readonly name: string;
    readonly craniocaudal: number;
    readonly oblique: number;
    readonly cameraTransform: ICameraTransform;
}

export class CarmView implements ICarmView {
    constructor(
        readonly id: string,
        readonly name: string,
        readonly craniocaudal: number,
        readonly oblique: number,
        readonly cameraTransform: ICameraTransform
    ) {
        Object.freeze(this.cameraTransform);
        Object.freeze(this);
    }
}

export class CarmViewFactory {
    static createFromVascularDescription(carmAngles: readonly ICarmAngleData[]): CarmView[] {
        const views: CarmView[] = [];
        for (let index = 0; index < carmAngles.length; index++) {
            const ca = carmAngles[index];
            if (!ca.cameraTransform?.position || !ca.cameraTransform?.focal || !ca.cameraTransform?.up) {
                console.warn(
                    `[CarmViewFactory] Skipping malformed C-arm angle entry at index ${index}: "${ca.name ?? 'unnamed'}"`
                );
                continue;
            }
            views.push(
                new CarmView(`precomputed-${index}`, ca.name, ca.craniocaudal, ca.oblique, {
                    position: ca.cameraTransform.position as Vec3,
                    focal: ca.cameraTransform.focal as Vec3,
                    up: ca.cameraTransform.up as Vec3,
                    angle: ca.cameraTransform.angle,
                })
            );
        }
        return views;
    }
}
